"use strict";

import Vue from "vue";
import { Fighter } from "./fighter.js";
import { findFighterDesign } from "@/data/fighterDesigns.js";
import { fixIosSvgFilters } from "@/utils/svgUtils";

const roundels = require.context("@/assets/roundels", false, /\.svg$/);
const fighters = require.context("@/assets/fighters", false, /\.svg$/);
const qrCodes = require.context("@/assets/qrCodes", false, /\.svg$/);

function roundelAssetPath(roundel) {
  return roundels("./" + roundel + ".svg");
}

function fighterAssetPath(fighter) {
  return fighters("./" + fighter + ".svg");
}

function qrCodeAssetPath(fighter) {
  return qrCodes("./" + fighter + ".svg");
}

export async function createFighterComponent(fighterName) {
  let [template, colors, digits, numbers, roundelSizes] =
    await createFighterTemplate(fighterName);

  let component = Vue.component("fighter", {
    template: template,
    props: {
      number: { type: String, default: "00" },
      roundel: { type: String, default: "france" },
      qrVisible: { type: Boolean, default: false },
      roundelScale: { type: Number, default: 2.0 },
    },
    data() {
      return {
        colors,
        digits,
        numbers,
        target: null,
        roundelSizes,
        uid: "",
      };
    },
    created() {
      console.log("fighterComponent created");
      this.uid = Math.random().toString(36).substr(2, 9);
      this.onChangeNumber(this.number);
      this.onChangeRoundelScale(this.roundelScale);
    },
    methods: {
      setTarget(target) {
        console.log(`setTarget("${target}")`);
        this.target = target;
        this.$emit("openColorInput", target);
      },
      getColor(target) {
        return this.colors[target];
      },
      getDigit(target) {
        return `#n${this.digits[target]}_${this.uid}`;
      },
      getNumber(target) {
        return this.numbers[target];
      },
      getRoundelWidth(target) {
        console.log(target);
        return this.roundelSizes[target].width * this.roundelScale;
      },
      getRoundelHeight(target) {
        console.log(target);
        return this.roundelSizes[target].height * this.roundelScale;
      },
      roundelPath(roundel) {
        return roundelAssetPath(roundel);
      },
      qrCodePath(fighter) {
        return qrCodeAssetPath(fighter);
      },
      onChangeNumber(number) {
        console.log(`onChangeNumber(${number})`);
        const d0 = number.charAt(0);
        const d1 = number.charAt(1);
        var digits = Object.assign({}, this.digits);
        digits["digit_InnerL"] = d0;
        digits["digit_OuterL"] = d0;
        digits["digit_InnerR"] = d1;
        digits["digit_OuterR"] = d1;
        this.digits = digits;

        numbers["number_OnTailL"] = number;
        numbers["number_OnTailR"] = number;
        this.numbers = numbers;
      },
      onChangeRoundelScale(scale) {
        console.log(`onChangeRoundelScale(${scale})`);
        // const values = Object.values(this.roundelSizes);
        // for (const v of values) {
        //   const el = v[0];
        //   const width = v[1];
        //   const height = v[2];
        //   el.removeAttribute("width");
        //   el.removeAttribute("height");
        //   el.setAttribute("width", width * scale);
        //   el.setAttribute("height", height * scale);
        // }
      },
    },
    watch: {
      number: {
        handler(newValue, oldValue) {
          if (newValue != oldValue) {
            this.onChangeNumber(newValue);
          }
        },
      },
      roundelScale: {
        handler(newValue, oldValue) {
          if (newValue != oldValue) {
            this.onChangeRoundelScale(newValue);
          }
        },
      },
    },
  });

  return {
    component,
    colors,
    digits,
    numbers,
  };
}

async function createFighterTemplate(fighterName) {
  const design = findFighterDesign(fighterName);
  const symbolColorSelectable = design ? design.symbolColorSelectable : false;
  const svgPath = fighterAssetPath(fighterName);
  const response = await fetch(svgPath);
  const svgText = await response.text();
  const fighter = new Fighter(svgText);

  fighter.svg.style.width = "100%";
  fighter.svg.style.height = "100%";

  // Global iOS Fix for SVG Filters (V1)
  fixIosSvgFilters(fighter.svg);



  const roundelSizes = {};
  for (const [k, v] of Object.entries(fighter.elements)) {
    // Symbol filter: if not selectable, ignore elements inside <symbol>
    if (!symbolColorSelectable) {
      if (v.el.closest("symbol")) continue;
    }

    if (v.roundel) {
      v.el.removeAttribute("xlink:href");
      v.el.setAttribute("v-bind:xlink:href", "roundelPath(roundel)");

      v.el.removeAttribute("width");
      v.el.setAttribute("v-bind:width", `getRoundelWidth("${k}")`);

      v.el.removeAttribute("height");
      v.el.setAttribute("v-bind:height", `getRoundelHeight("${k}")`);

      roundelSizes[k] = { width: v.width, height: v.height };
    }

    if (v.qr) {
      v.el.removeAttribute("xlink:href");
      v.el.setAttribute("v-bind:xlink:href", `qrCodePath('${fighterName}')`);
      v.el.setAttribute("v-if", "qrVisible");
    }

    if (v.fill) {
      v.el.removeAttribute("fill");
      v.el.setAttribute("v-bind:fill", `getColor("${k}")`);
      v.el.setAttribute("v-on:click.prevent", `setTarget("${k}")`);
    }

    if (v.stroke) {
      v.el.removeAttribute("stroke");
      v.el.setAttribute("v-bind:stroke", `getColor("${k}")`);
      v.el.setAttribute("v-on:click.prevent", `setTarget("${k}")`);
    }

    if (v.stopColor) {
      v.el.removeAttribute("stop-color");
      v.el.setAttribute("v-bind:stop-color", `getColor("${k}")`);
      v.el.setAttribute("v-on:click.prevent", `setTarget("${k}")`);
    }

    if (v.digit) {
      v.el.removeAttribute("xlink:href");
      v.el.setAttribute("v-bind:xlink:href", `getDigit("${k}")`);
    }

    if (v.number) {
      const tspans = v.el.getElementsByTagName("tspan");
      tspans[0].innerHTML = `{{ getNumber("${k}") }}`;
    }
  }

  // Scoping IDs for Print Support
  // 1. Collect all IDs
  const allElements = fighter.svg.getElementsByTagName("*");
  const ids = new Set();
  for (let i = 0; i < allElements.length; i++) {
    if (allElements[i].id) {
      ids.add(allElements[i].id);
    }
  }

  // 2. Update references and definitions
  for (let i = 0; i < allElements.length; i++) {
    const el = allElements[i];

    // Update fill="url(#id)"
    const fill = el.getAttribute("fill");
    if (fill && fill.startsWith("url(#")) {
      const id = fill.substring(5, fill.length - 1);
      if (ids.has(id)) {
        el.removeAttribute("fill");
        el.setAttribute("v-bind:fill", "`url(#${" + "'" + id + "_' + uid})`");
      }
    }

    // Update stroke="url(#id)"
    const stroke = el.getAttribute("stroke");
    if (stroke && stroke.startsWith("url(#")) {
      const id = stroke.substring(5, stroke.length - 1);
      if (ids.has(id)) {
        el.removeAttribute("stroke");
        el.setAttribute("v-bind:stroke", "`url(#${" + "'" + id + "_' + uid})`");
      }
    }

    // Update xlink:href="#id"
    const href = el.getAttribute("xlink:href");
    if (href && href.startsWith("#")) {
      const id = href.substring(1);
      if (ids.has(id)) {
        el.removeAttribute("xlink:href");
        el.setAttribute("v-bind:xlink:href", "`#${" + "'" + id + "_' + uid}`");
      }
    }

    // Update the ID definition itself
    // We check if it hasn't been removed by previous logic (though unlikely to conflict)
    if (el.id && ids.has(el.id)) {
      const oldId = el.id;
      el.removeAttribute("id");
      el.setAttribute("v-bind:id", `'${oldId}_' + uid`);
    }
  }

  const template = fighter.svg.outerHTML;

  const colors = {};
  for (const [k, v] of Object.entries(fighter.elements)) {
    // Basic filter: ignore <image>
    if (v.el.nodeName === "image") continue;

    // Symbol filter: if not selectable, ignore elements inside <symbol>
    if (!symbolColorSelectable) {
      if (v.el.closest("symbol")) continue;
    }

    if (v.fill) {
      colors[k] = v.fill;
    } else if (v.stroke) {
      colors[k] = v.stroke;
    } else if (v.stopColor) {
      colors[k] = v.stopColor;
    }
  }


  const digits = {};
  for (const [k, v] of Object.entries(fighter.elements)) {
    if (v.digit) {
      digits[k] = v.digit;
    }
  }

  const numbers = {};
  for (const [k, v] of Object.entries(fighter.elements)) {
    if (v.number) {
      numbers[k] = v.number;
    }
  }

  return [template, colors, digits, numbers, roundelSizes];
}
